<template>
  <div
    ref="container"
    class="game-offer-currency container">
    <div class="game-offer-currency__head">
      <h2 class="game-offer-currency__title">
        <template v-if="productOffers.total > 0">
          {{ productOffers.total }} {{ offersCountText }}
        </template>
        <template v-else>
          No offers found
        </template>
      </h2>

      <div class="game-offer-currency__filters">
        <currency-filters-row
          v-if="filterFields"
          :key="`filter_${product.id}`"
          class="game-offer__currency-row"
          :currency-settings="filterFields"
          currency-field-type="component_view"
          should-set-default-values
          @input="handleFilterEmit" />

        <ui-kit-select
          v-model="currencyQuery.orderBy"
          :items="orderByList"
          value-field="id"
          class="currency-field
            game-offer-currency__component
            game-offer-currency__component--default
            game-offer-currency__component--ui-kit-select" />

        <label
          class="currency-field
            game-offer-currency__component
            game-offer-currency__component--online">
          Seller online
          <ui-switcher
            v-model="currencyQuery.onlineNow"
            class="game-offer-currency__online-switcher" />
        </label>
      </div>
    </div>

    <ul
      v-if="currencyOffers.length && !isLoading"
      class="game-offer-currency__list">
      <base-currency-offer
        v-for="item in currencyOffers"
        :key="item.id"
        :currency-settings="product.product_currency_setting"
        :offer-data="item"
        tag="li"
        currency-field-type="component_view">
        <template #default="{ filtersList }">
          <game-offer-currency-item
            :currency-item="item"
            :step-size="product.product_currency_setting.step_size"
            :primary-field="getPrimary(item, filtersList)"
            :currency-title="product.product_currency_setting.title !== 'Gold' ?
              product.product_currency_setting.title.toLowerCase() : product.product_currency_setting.title"
            class="game-offer-currency__item"
            @select-currency="openCurrencyPopup" />
        </template>
      </base-currency-offer>
    </ul>

    <ui-pagination
      v-if="productOffers.total > itemsPerPage"
      v-model="currencyQuery.page"
      :total="productOffers.total"
      :items-per-page="itemsPerPage"
      class="game-offer-currency__pagination"
      @paginate="scrollToTop()" />

    <!-- static content -->
    <how-it-works
      :titles="howItWorksContent"
      class="game-currency-offer__how" />

    <product-description
      :product="product"
      :show-navigation="false"
      :reviews-slider-props="reviewsSliderProps"
      class="game-currency-offer__description" />
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import { extractIdFromSlug } from 'Root/helpers/product';
import { scrollTo } from 'Root/helpers/tools';
import axios from 'axios';
import PaymentSystemModel from 'Models/PaymentSystemModel';
import yandexMetrika from 'Root/drivers/yandexMetrika';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import HowItWorks from 'Root/pages/game/components/HowItWorks.vue';
import GoldDetailsPopup from 'Root/pages/game/components/GoldDetailsPopup.vue';
import ProductDescription from 'Root/pages/game/components/ProductDescription.vue';
import GameOfferCurrencyItem from 'Root/pages/game/gameOffer/GameOfferCurrencyItem.vue';
import UiKitSelect from 'Components/ui/Select/UiKitSelect.vue';
import UiSwitcher from 'Components/ui/Switcher/UiSwitcher.vue';
import UiPagination from 'Components/ui/Pagination/UiPagination.vue';
import BaseCurrencyOffer from 'Components/Currency/BaseCurrencyOffer.vue';
import CurrencyFiltersRow from 'Components/Currency/CurrencyFiltersRow.vue';
import GameModel from 'Models/GameModel';
import UserModel from 'Models/UserModel';
import CurrencyOfferModel from 'Models/CurrencyOfferModel';
import tiktokTagAnalytics from 'Root/drivers/tiktokTagAnalytics';
import { ABTestGoldPriceIncreaseForNewUsers, ABTestGoldPriceIncreaseForOldUsers } from 'Root/drivers/ABTestsDriver';
import {
  AB_TEST_GOLD_PRICE_INCREASE_FOR_NEW_USER_ID,
  AB_TEST_GOLD_PRICE_INCREASE_FOR_OLD_USER_ID,
} from 'Root/drivers/ABTestsDriver/ABTestConstants';

let currencyOffersAbortController = null;

const INITIAL_FILTER_SETTINGS = {
  page: 1,
  orderBy: 'price_per_step#asc',
  onlineNow: true,
  filters: {},
};

const initQueryFilters = (filterFields) => {
  if (!filterFields) {
    return null;
  }

  const ID_TO_FIELDS_MAP = {};
  const filters = {};

  filterFields.product_currency_setting_fields.forEach((field) => {
    ID_TO_FIELDS_MAP[field.id] = field;
  });

  filterFields.product_currency_setting_fields.forEach((setting) => {
    if (
      setting.default_value_id === null
            || (setting.parent_id
                    && setting.parent_value_id
                    && ID_TO_FIELDS_MAP[setting.parent_id].default_value_id !== setting.parent_value_id)
    ) {
      return;
    }

    const defaultValue = setting.product_currency_setting_field_values.find(
      (value) => value.id === setting.default_value_id,
    );

    if (defaultValue) {
      filters[setting.id] = defaultValue.value;
    }
  });

  return filters;
};

const filterFieldsSSR = (product, activeRegion) => {
  if (!product?.product_currency_setting_fields) {
    return null;
  }

  // матчим регион с регионом в фильтре при заходе в товар
  // переключаем на регион из шапки если он отличается от дефолта
  if (+product.product.game === GameModel.WOW.id
    || +product.product.game === GameModel.WOW_WOTLK.id
    || +product.product.game === GameModel.WOW_CATACLYSM.id
  ) {
    const regionField = product.product_currency_setting_fields
      .find((field) => field.title === 'Region');

    if (regionField?.default_value_id) {
      let regionDefaultValue;
      let activeRegionValue;

      regionField.product_currency_setting_field_values
        .forEach((value) => {
          if (value.id === regionField.default_value_id) {
            regionDefaultValue = value;
          }

          if (
            activeRegion.regionCode === value.value
                || activeRegion.shortTitle === value.value
                || activeRegion.title === value.value
          ) {
            activeRegionValue = value;
          }
        });

      if (regionDefaultValue.id !== activeRegionValue.id) {
        regionField.default_value_id = activeRegionValue.id;
      }
    }
  }

  return product;
};

export default {
  name: 'GameOfferCurrency',

  components: {
    BaseCurrencyOffer,
    CurrencyFiltersRow,
    UiKitSelect,
    UiSwitcher,
    // eslint-disable-next-line vue/no-unused-components
    GameOfferCurrencyItem,
    UiPagination,
    HowItWorks,
    ProductDescription,
  },

  props: {
    isSplitView: {
      type: Boolean,
      default: false,
    },
  },

  asyncData({ context, store, router }) {
    const { CancelToken } = axios;
    const currencyOffersAbortControllerAsync = CancelToken.source();

    const fullSlug = router.currentRoute.params.id;
    const productId = router.currentRoute.meta?.tagAsOfferInfo?.offerId || extractIdFromSlug(fullSlug);
    const currencySettings = context.getCurrencySettings()[productId];
    const filters = initQueryFilters(filterFieldsSSR(currencySettings, store.state.activeRegion));

    // этот компонент динамически подгружается в зависимости от условия в GameOffer.vue в offerComponent
    // asyncData вызывается, если указанный компонент импортирован в GameOffer.vue
    // это вызывает баг, который вызывает asyncData компонента, а этот компонент даже не должен быть отрисован
    // поэтому делаем проверку на наличие currencySettings и тип маркетплейса
    if (currencySettings?.marketplace_type !== CurrencyOfferModel.TYPES.GOLD) {
      return null;
    }

    if (!filters) {
      return null;
    }

    const params = {
      limit: 20,
      json_data: filters,
      offset: 0,
    };

    if (INITIAL_FILTER_SETTINGS.orderBy !== 'isRecommended') {
      const [sortColumn, sortDirection] = INITIAL_FILTER_SETTINGS.orderBy.split('#');

      params.sort_column = sortColumn;
      params.sort_direction = sortDirection;
    } else {
      params.isRecommended = true;
      params.sort_column = 'price_per_step';
      params.sort_direction = 'asc';
    }

    if (INITIAL_FILTER_SETTINGS.onlineNow) {
      params.is_online = true;
    }

    return store.dispatch('products/getCurrencyOffersAction',
      { id: currencySettings.id, params, cancelToken: currencyOffersAbortControllerAsync.token });
  },

  data() {
    return {
      isLoading: false,
      itemsPerPage: 20,
      stripe: null,
      stripePaymentRequest: null,
      availableStripePaymentMethods: {
        googlePay: false,
      },

      /* Filters and Sorting */
      currencyQuery: {
        page: 1,
        orderBy: 'price_per_step#asc',
        onlineNow: true,
        filters: {},
      },
      orderByList: [
        {
          id: 'price_per_step#asc',
          title: 'Cheapest price',
        },
        {
          id: 'isRecommended',
          title: 'Recommended',
        },
        {
          id: 'guarantee_delivery_time#asc',
          title: 'Fastest delivery',
        },
        {
          id: 'amount#desc',
          title: 'Largest stock',
        },
      ],

      /* static */
      howItWorksContent: [
        'Choose amount of Gold',
        'Fill in your details',
        'Make a payment',
        'Receive your Gold',
        'Conﬁrm delivery',
      ],

      reviewsSliderProps: {
        perView: 4,
        breakpoints: {
          1700: {
            perView: 3,
          },
        },
      },
    };
  },

  computed: {
    ...mapState(['activeRegion', 'user', 'userDevice', 'countryCode']),
    ...mapState('products', ['product', 'tags', 'productOffers']),
    ...mapGetters('products', ['filterFields']),

    isMobile() {
      return this.userDevice.innerWidth < 610;
    },

    offersCountText() {
      return `offer${this.currencyOffers.length !== 1 ? 's' : ''} available`;
    },

    currencyOffers() {
      return this.productOffers.data && this.productOffers.data.length ? this.productOffers.data : [];
    },
  },

  watch: {
    isMobile() {
      this.currencyQuery.page = 1;
    },

    product(newValue, oldValue) {
      if (!oldValue || (newValue && newValue.id !== oldValue.id)) {
        this.currencyQuery = JSON.parse(JSON.stringify(INITIAL_FILTER_SETTINGS));

        const filters = initQueryFilters(this.filterFields);
        Object.keys(filters).forEach((key) => {
          this.$set(this.currencyQuery.filters, key, filters[key]);
        });
      }
    },

    filterFields(newValue, oldValue) {
      if (!oldValue || (newValue && newValue.id !== oldValue.id)) {
        this.currencyQuery = JSON.parse(JSON.stringify(INITIAL_FILTER_SETTINGS));

        const filters = initQueryFilters(this.filterFields);
        Object.keys(filters).forEach((key) => {
          this.$set(this.currencyQuery.filters, key, filters[key]);
        });
      }
    },
  },

  async created() {
    try {
      this.isLoading = true;
      const isLoggedIn = !!this.user;
      const isCustomer = this.user?.role === UserModel.ROLES.CUSTOMER.id;
      const paidOrdersCount = this.user?.customer_counters?.paid_orders_count ?? 0;

      const hasABInvolved = isLoggedIn
          && this.user.ab_test_involved?.some((ab) => {
            const isABNewInvolved = ab.id === AB_TEST_GOLD_PRICE_INCREASE_FOR_NEW_USER_ID;
            const isABOldInvolved = ab.id === AB_TEST_GOLD_PRICE_INCREASE_FOR_OLD_USER_ID;

            return isABNewInvolved || isABOldInvolved;
          });

      if (!hasABInvolved) {
        const isNewCustomer = !isLoggedIn || (isCustomer && paidOrdersCount === 0);
        const isOldCustomer = isCustomer && paidOrdersCount > 0;

        if (isNewCustomer) {
          await ABTestGoldPriceIncreaseForNewUsers.addUserToTest(this.$ssrContext);
        }

        if (isOldCustomer) {
          await ABTestGoldPriceIncreaseForOldUsers.addUserToTest(this.$ssrContext);
        }
      }

      const filters = initQueryFilters(this.filterFields);

      if (filters) {
        Object.keys(filters).forEach((key) => {
          this.$set(this.currencyQuery.filters, key, filters[key]);
        });
      }

      if (typeof window !== 'undefined') {
        this.sendAnalytics();

        if (!this.currencyOffers.length) {
          this.getCurrencyOffers();
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  },

  mounted() {
    this.$watch('currencyQuery', {
      handler() {
        this.getCurrencyOffers();
      },
      deep: true,
    });

    if (window.Stripe) {
      this.checkStripeAvailableMethods();
    } else {
      const stripeScript = document.getElementById('stripe-script');
      if (!stripeScript) {
        return;
      }

      stripeScript.onload = () => {
        this.checkStripeAvailableMethods();
      };
    }
  },

  beforeDestroy() {
    this.mutateProductOffers({ offers: [], total: 0 });
  },

  methods: {
    ...mapMutations('products', ['mutateProductOffers']),
    ...mapActions('products', ['getCurrencyOffersAction']),

    handleFilterEmit(event) {
      this.currencyQuery.page = 1;

      if (event?.data) {
        this.currencyQuery.filters = event.data;
      } else {
        this.currencyQuery.filters = {};
      }
    },

    parsePriceToStripeAmount(price) {
      let amount = 0;

      if (String(price).includes('.')) {
        const splittedPrice = String(price).split('.');
        amount = Number(splittedPrice[0]) * 100 + Number(splittedPrice[1]);
      } else {
        amount = price * 100;
      }

      return +amount;
    },

    async checkStripeAvailableMethods() {
      this.stripe = window.Stripe(PaymentSystemModel.STRIPE_PUBLIC_KEY);

      const country = this.countryCode;
      const currency = this.activeRegion.currency.title.toLowerCase();

      if (!country) {
        return;
      }

      this.stripePaymentRequest = this.stripe.paymentRequest({
        country,
        currency,
        /**
         * {@link https://stripe.com/docs/js/appendix/payment_item_object}
         */
        total: {
          label: this.product?.title,
          amount: this.parsePriceToStripeAmount(10),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      let canMakePaymentVia = null;

      try {
        canMakePaymentVia = await this.stripePaymentRequest.canMakePayment();
        // eslint-disable-next-line no-empty
      } catch (e) {}

      if (canMakePaymentVia) {
        if (Object.keys(canMakePaymentVia).includes('googlePay')) {
          this.availableStripePaymentMethods.googlePay = true;
        }
      }
    },

    getCurrencyOffers() {
      if (currencyOffersAbortController !== null) {
        currencyOffersAbortController.cancel();
      }

      const { CancelToken } = this.$axios;
      currencyOffersAbortController = CancelToken.source();

      const params = {
        limit: this.itemsPerPage,
        json_data: this.currencyQuery.filters,
        offset: (this.currencyQuery.page - 1) * this.itemsPerPage,
      };

      if (this.currencyQuery.orderBy !== 'isRecommended') {
        const [sortColumn, sortDirection] = this.currencyQuery.orderBy.split('#');

        params.sort_column = sortColumn;
        params.sort_direction = sortDirection;
      } else {
        params.isRecommended = true;
        params.sort_column = 'price_per_step';
        params.sort_direction = 'asc';
      }

      if (this.currencyQuery.onlineNow) {
        params.is_online = true;
      }

      this.getCurrencyOffersAction({
        id: this.product.product_currency_setting_id,
        params,
        cancelToken: currencyOffersAbortController.token,
      });
    },

    openCurrencyPopup(currencyItem) {
      this.$vuedals.openMobilePanel({
        name: 'gold-details-popup',
        class: 'gold-detail-bg',
        component: GoldDetailsPopup,
        data: {
          product: this.product,
          offer: currencyItem,
          stripeData: {
            stripe: this.stripe,
            stripePaymentRequest: this.stripePaymentRequest,
            availableStripePaymentMethods: this.availableStripePaymentMethods,
            parsePriceToStripeAmount: this.parsePriceToStripeAmount,
          },
          isSplitView: this.isSplitView,
        },
      });

      googleTagAnalytics.fireBuyClick('buy_now_1', this.product.title);
      tiktokTagAnalytics.fireAddToCart(this.user, this.price, this.activeRegion.currency.title);
    },

    sendAnalytics() {
      const productTag = this.tags.find((tag) => tag.id === this.product.master_tag_id);
      googleTagAnalytics.firePageView(this.product, productTag, this.product.cost_usa);
      tiktokTagAnalytics.fireViewContent(this.user);

      if (this.isSplitView) {
        googleTagAnalytics.fireGtagEvent('view', 'split_marketplace_purchase_new', null);
        yandexMetrika.invokeEvent('split_marketplace_page_view_new');
      }
    },

    getPrimary(offer, filters) {
      const primaryItemId = filters.find((filter) => filter.fieldData.is_primary)?.fieldData.id;

      return offer.data[primaryItemId];
    },

    scrollToTop() {
      const y = this.$refs.container.offsetTop || 0;
      scrollTo(0, y);
    },
  },
};
</script>

<style lang="scss" scoped>
.game-offer-currency {
  display: grid;
  grid-gap: 16px;
  position: relative;
  z-index: 2;
}

.game-offer-currency__filters {
  --gap-x: 8px; // Всё ещё игнорируем гэп флекс
  --gap-y: 16px;

  width: 100%;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  margin: calc(-1 * var(--gap-y) / 2) calc(-1 * var(--gap-x) / 2);
}

.game-offer-currency__head {
  display: grid;
  grid-gap: 16px;
}

.game-offer-currency__title {
  font-size: 17px;
  font-weight: 500;
}

.game-offer__currency-row {
  margin: calc(var(--gap-y) / 2) calc(var(--gap-x) / 2);
}

.game-offer__currency-row /deep/ > .currency-filters-row {
  display: flex;
  grid-gap: 0;
  flex-wrap: wrap;
  margin: calc(-1 * var(--gap-y) / 2) calc(-1 * var(--gap-x) / 2);
}

.game-offer-currency__filters /deep/ .currency-field {
  --font-size: 13px;
  --height: 40px;
  --border-radius: 4px;
  --input-bg-color: #{rgba($color--basic-blue, 0.25)};
  --elements-background-color: #29335a;
  --background-color--focused: #29335a;
  --background-color: #{$color--basic-blue};
  --container-padding: 5px;
  --button-padding: 5px 8px;
  --input-padding: 10px;
  --bg-color: #{rgba($color--basic-blue, 0.25)};
  --bg-color-head--open: 228, 37%, 26%;
  --bg-color-elements: #29335a;
  --head-elements-color--open: #{$color--basic-white};
  --font-weight: 500;
  --width: 10px;

  width: 100%;
  grid-gap: 0;
  margin: calc(var(--gap-y) / 2) calc(var(--gap-x) / 2);

  .ui-select__separator {
    display: none;
  }

  .switcher-tabs {
    display: grid;
  }

  .search-input {
    display: block;
    font-size: 13px;
  }

  .ui-input__prefix {
    display: none !important;
  }

  .ui-input__wrap-inner,
  .ui-select__head {
    border: 2px solid rgba($color--basic-blue, 0.25);
  }

  .ui-input__wrap {
    --placeholder-color: #{$color--basic-white};
  }

  .search-input__arrow {
    --width: 7.2px;
    --shape-color: currentColor;
  }

  .base-select__items {
    z-index: -1;
  }
}

  .game-offer-currency__component--ui-kit-select {
    min-width: 164px;
  }

  .game-offer-currency__component--online {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    font-size: 15px;
  }

.game-offer-currency__list {
  --columns: auto-fit;

  display: grid;
  grid-gap: 16px;
  grid-template-columns: repeat(var(--columns, auto-fit), minmax(280px, 1fr));
}

.game-offer-currency__item {
  cursor: pointer;
}

.game-offer-currency__pagination {
  margin-top: 16px;
}

.game-currency-offer__how {
  /* TODO: fix */
  --steps-template-columns: repeat(5, minmax(0, 1fr)) !important;

  margin-bottom: 20px;
}

/deep/ .game-currency-offer__description {
  overflow: hidden;

  .product-description__item {
    padding: 0;
  }
}

  @media only screen and #{$screen-less-than-640} {
    /deep/ .currency-filters-row {
      .currency-field.currency-filters-row--inline,
      .currency-field.currency-filters-row--inline + .currency-field {
        width: unset;
        flex-grow: 1;
      }
    }

    .game-offer-currency__component--online {
      font-size: 13px;
    }
  }

@media only screen and #{$screen-more-or-640} {
  /deep/ .currency-filters-row {
    .currency-field.currency-filters-row--inline,
    .currency-field.currency-filters-row--inline + .currency-field {
      flex-grow: 0;
    }
  }

  .game-offer-currency__filters {
    flex-direction: row;
  }

  .game-offer-currency__filters /deep/ .currency-field {
    width: unset;
  }

  .game-offer-currency__filters /deep/ .currency-field.ui-kit-select,
  .game-offer-currency__filters /deep/ .currency-field.search-input {
    flex-grow: 0;
    //flex-basis: 0;
    max-width: 600px;
    min-width: 150px;
  }

  .game-offer-currency__component--online {
    flex-direction: row-reverse;

    /deep/ .game-offer-currency__online-switcher {
      margin-right: 8px;
    }
  }
}

@media only screen and #{$screen-more-or-1024} {
  .game-offer-currency__filters {
    --gap-x: 24px;
    --gap-y: 12px;
  }

  .game-offer-currency {
    grid-gap: 40px;
    margin-bottom: 40px;
  }

  .game-offer-currency__head {
    grid-gap: 24px;
  }

  .game-offer-currency__title {
    font-size: 25px;
  }

  .game-offer-currency__list {
    --columns: 1;
  }

  .game-currency-offer__how {
    margin-bottom: 40px;
  }

  .game-offer-currency__filters /deep/ .currency-field {
    --font-size: 15px;
    --button-padding: 8px 16px;
    --input-padding: 12px;
    --height: 48px;
  }
}
</style>
